import { EAvailableLanguages } from '@/configuration/lang/types';
import { Response } from '@/services/config/types';
import { clearFollowUps, reconcileOptimisticMessage, setFollowUps, upsertManyMessages } from '@/store/chat/chat.slice';
import { TMessage, TSendMessageResponse } from '@/store/chat/types';
import { clearThreadLoading, reconcileServerThread } from '@/store/thread/threadSlice';
import Logger from '@/utils/logger';
import { toAvailableLanguagesMapper } from '@/utils/mappers/languagesMapper';
import { isTemporaryMessageId, isTemporaryThreadId } from '@/utils/temporaryUtils/temporaryIds';

import { api } from '../config/config';
import { TRouteRequest, TRouteResponse } from './types';

const LOGGER_TAG = 'ChatApi';

export const chatApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // INFO: Side-ffects of this mutation are handled both by the chat.listener and onQueryStarted
    sendMessage: builder.mutation<TSendMessageResponse, Partial<TRouteRequest>>({
      query: (message) => {
        const mappedMessage = {
          ...message,
          user_id: message.userId,
          thread_id: message.threadId && message.threadId.length > 0 ? message.threadId : null,
        };
        delete mappedMessage.userId;
        delete mappedMessage.threadId;
        delete mappedMessage.optimisticMessageId;
        return {
          url: '/v1/route/',
          method: 'POST',
          body: mappedMessage,
        };
      },

      transformResponse: (response: Response<TRouteResponse>): TSendMessageResponse => {
        if (response.data && response.data.thread_id) {
          const threadId = response.data.thread_id;
          const followUps = response.data.messages?.follow_ups ?? [];

          const updatedMessages = response.data.messages.content.map(
            (msg): TMessage => ({
              content: msg.content,
              threadId: threadId,
              role: msg.role,
              data_visualization: msg.data_visualization,
              follow_ups: followUps,
              timestamp: msg.last_updated,
              messageId: msg.message_id,
              derivesFromPromptLibrary: msg.prompt_library,
              language: toAvailableLanguagesMapper(msg.language ?? EAvailableLanguages.EN),
            }),
          );

          return {
            status: response.status,
            serverMessage: response.message,
            messages: updatedMessages,
            threadId: response.data?.thread_id ?? null,
            followUps,
          };
        }

        return {
          status: response.status,
          serverMessage: response.message,
          messages: [],
          threadId: null,
          followUps: [],
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        dispatch(clearFollowUps());
        try {
          const { data } = await queryFulfilled;

          if (data.messages.length === 0) return;

          if (data.threadId && arg.optimisticMessageId && isTemporaryMessageId(arg.optimisticMessageId)) {
            const humanMessageIndex = data.messages.findIndex((msg) => msg.role === 'human');
            if (humanMessageIndex !== -1) {
              const humanMessage = data.messages[humanMessageIndex];
              const nextMesage = data.messages[humanMessageIndex + 1]; // INFO: The next message after a human message
              const isAiReply = nextMesage.role === 'ai';

              dispatch(
                reconcileOptimisticMessage({
                  optimisticMessageId: arg.optimisticMessageId,
                  serverMessageId: humanMessage.messageId,
                  threadId: data.threadId,
                  language: isAiReply ? toAvailableLanguagesMapper(nextMesage.language) : EAvailableLanguages.EN,
                }),
              );
            }
          }

          if (data.threadId && arg.threadId && isTemporaryThreadId(arg.threadId)) {
            dispatch(reconcileServerThread({ serverThreadId: data.threadId }));
          } else if (arg.threadId) {
            dispatch(clearThreadLoading(arg.threadId));
          }

          dispatch(upsertManyMessages(data.messages));

          if (data.followUps && data.followUps.length > 0) {
            dispatch(setFollowUps({ threadId: data.threadId!, items: data.followUps }));
          }
        } catch (error) {
          Logger.error(LOGGER_TAG, 'Failed to reconcile ids', error);
        }
      },
    }),
  }),
});

const { useSendMessageMutation } = chatApi;

const SEND_MESSAGE_CACHE_KEY = 'SEND_MESSAGE_CACHE_KEY';

export const useSendQuestionMutation = () => useSendMessageMutation({ fixedCacheKey: SEND_MESSAGE_CACHE_KEY });
